import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useSnackbar } from "notistack";
import TableView from "../../../util/TableView";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { Backdrop } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { AdminActionButtons } from "./AdminActionButtons";
import SecretSantaLocationDetailDialog from "../../../dialog/SecretSantaLocationDetailDialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { api } from "../../../../api/api";
import LinkIcon from '@mui/icons-material/Link';
import { copyToClipboard } from "../../../../api/helper";
import SantasStatusCounts from "../common/SantasStatusCounts";


const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },

}));

export function SecretSantaLocations(props) {
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const snackBarAddMessage = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [backdropOpen, setBackdropOpen] = React.useState(false);

  const handleRowClick = (location) => {
    setSelectedLocationId(location.id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedLocationId(null);
  };

  function handleLocationActivation(loc) {
    return async () => {
      try {
        setBackdropOpen(true);
        if (loc.isActive) {
          await api.secretSanta.deactivateLocation(loc.id);
          snackBarAddMessage(`Location "${loc.name}" deactivated`, 'success');
        } else {
          await api.secretSanta.activateLocation(loc.id);
          snackBarAddMessage(`Location "${loc.name}" activated`, 'success');
        }
        props.setReload(true);
      } catch (error) {
        console.log(error);
        snackBarAddMessage(`Unable to ${loc.isActive ? 'deactivate' : 'activate'} location. Error: ${error.status ?? ''}`, 'error');
      } finally {
        setBackdropOpen(false);
      }
    };
  }

  const tableHeader = (
    <TableRow>
      <TableCell>Active</TableCell>
      <TableCell>Name</TableCell>
      <TableCell>Santas count</TableCell>
      <TableCell>Exact destination for present</TableCell>
      <TableCell>Reindeers</TableCell>
      <TableCell>Actions</TableCell>
    </TableRow>
  );

  const tableRows = (loc, _) => {
    return (
      <TableRow
        key={loc.id}
        onClick={() => handleRowClick(loc)}
        style={{
          cursor: 'pointer',
          opacity: loc.isActive ? 1 : 0.4
        }}
      >
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Tooltip title={loc.isActive ? "Deactivate location" : "Activate location"}>
            <IconButton
              size="small"
              onClick={handleLocationActivation(loc)}
              color={loc.isActive ? 'success' : 'default'}
            >
              {loc.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          {loc.isActive &&
            <Tooltip title={"Copy direct link"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const host = window.location.host;
                  const link = `${host}/secretSanta/${loc.id}`;
                  copyToClipboard(link)
                    .then(() => snackBarAddMessage('Link copied to clipboard', 'success'))
                    .catch(() => snackBarAddMessage('Failed to copy link', 'error'));
                }}
                style={{ marginLeft: 6 }}
              >
                <LinkIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>}
        </TableCell>
        <TableCell>
          {loc.name}
        </TableCell>
        <TableCell>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{loc.santasCount}</span>
          </div>
          <SantasStatusCounts santasCountByStatus={loc.santasCountByStatus}
                              santasCount={loc.santasCount}
          />
        </TableCell>
        <TableCell>{loc.lockersSupported ? `📦: ${loc.exactAddress}` : "🎄"}</TableCell>
        <TableCell>{loc.reindeers ? loc.reindeers : "🤷‍♂️"}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {loc.isActive
            && <AdminActionButtons
              locationId={loc.id}
              locationName={loc.name}
              lockersSupported={loc.lockersSupported}
              santaState={loc.santaState}
              setRequestSantaState={props.setReload}
              setBackdropOpen={setBackdropOpen}
            />}
        </TableCell>
      </TableRow>
    );
  };


  return <Paper className={classes.paper}>
    <Backdrop
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={backdropOpen}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
    <Typography variant="h6" gutterBottom>
      Locations info
    </Typography>
    <TableView
      fetchUrl={"secretsanta/location/all"}
      tableHeader={tableHeader}
      tableRows={tableRows}
      data={data}
      setData={setData}
      defaultPageSize={100}
      reload={props.reload}
      setReload={props.setReload}
      {...props}
    />
    <SecretSantaLocationDetailDialog
      open={dialogOpen}
      handleClose={handleDialogClose}
      locationId={selectedLocationId}
      setBackdropOpen={setBackdropOpen}
      setReload={props.setReload}
    />
  </Paper>;
}
